/**
 * GridDropZone - Drop target for a single grid cell
 * Shows visual feedback when dragging elements over it
 */

import React, { memo } from "react";
import { Box } from "@mui/joy";
import type { CanvasGridConfig } from "../../types";
import { CANVAS_COLOR } from "../../theme/canvasTokens";

interface GridDropZoneProps {
  row: number;
  column: number;
  isOccupied: boolean;
  isTarget?: boolean;
  isInvalidTarget?: boolean;
  config: CanvasGridConfig;
}

/**
 * Individual drop zone for a grid cell.
 * Only rendered during drag operations.
 * Uses custom collision detection from parent instead of dnd-kit's useDroppable.
 */
function GridDropZoneComponent({
  row,
  column,
  isOccupied,
  isTarget,
  isInvalidTarget,
  config,
}: GridDropZoneProps) {
  const { rowHeight, gap } = config;

  // Calculate styles based on target state
  const getBgColor = () => {
    if (isInvalidTarget) return CANVAS_COLOR.dropTargetInvalidBg; // danger channel
    if (isTarget) return CANVAS_COLOR.dropTargetValidBg; // primary channel
    return "transparent";
  };

  const getBorderColor = () => {
    if (isInvalidTarget) return "danger.500";
    if (isTarget) return "primary.500";
    return "transparent";
  };

  return (
    <Box
      sx={{
        position: "absolute",
        top: (row - 1) * (rowHeight + gap),
        left: `calc(${((column - 1) / config.columns) * 100}%)`,
        width: `calc(${(1 / config.columns) * 100}%)`,
        height: rowHeight,
        bgcolor: getBgColor(),
        border: isTarget ? "2px dashed" : "none",
        borderColor: getBorderColor(),
        borderRadius: "sm",
        transition: "background-color 0.05s, border-color 0.05s",
        zIndex: isTarget ? 10 : 1,
        pointerEvents: "none", // Don't interfere with drag detection
      }}
    />
  );
}

export const GridDropZone = memo(GridDropZoneComponent);
export default GridDropZone;
